import React, { useRef, useMemo, useEffect, useState, Suspense } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

function useTheme() {
  const get = () =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark")
      ? "dark"
      : "light";
  const [theme, setTheme] = useState(get);
  useEffect(() => {
    const onChange = (e) => setTheme(e.detail);
    const onClass = () => setTheme(get());
    window.addEventListener("themechange", onChange);
    const obs = new MutationObserver(onClass);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => {
      window.removeEventListener("themechange", onChange);
      obs.disconnect();
    };
  }, []);
  return theme;
}

function fibonacciSphere(count, radius) {
  const pts = [];
  const phi = Math.PI * (Math.sqrt(5) - 1);
  for (let i = 0; i < count; i++) {
    const y = 1 - (i / (count - 1)) * 2;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = phi * i;
    pts.push(
      new THREE.Vector3(
        Math.cos(theta) * r * radius,
        y * radius,
        Math.sin(theta) * r * radius
      )
    );
  }
  return pts;
}

function buildConnections(nodes, threshold, max) {
  const idx = [];
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      if (nodes[i].distanceTo(nodes[j]) < threshold) {
        idx.push(i, j);
        if (idx.length / 2 >= max) return idx;
      }
    }
  }
  return idx;
}

function Globe({ dark }) {
  const inner = useRef(); // auto-rotation
  const outer = useRef(); // mouse parallax
  const { viewport } = useThree();

  const radius = 1.55;
  const nodes = useMemo(() => fibonacciSphere(38, radius), [radius]);
  const connectionIdx = useMemo(
    () => buildConnections(nodes, radius * 0.82, 55),
    [nodes, radius]
  );

  const lineGeo = useMemo(() => {
    const positions = new Float32Array(connectionIdx.length * 3);
    for (let k = 0; k < connectionIdx.length; k++) {
      const v = nodes[connectionIdx[k]];
      positions[k * 3] = v.x;
      positions[k * 3 + 1] = v.y;
      positions[k * 3 + 2] = v.z;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    return g;
  }, [nodes, connectionIdx]);

  useFrame((state, delta) => {
    if (inner.current) inner.current.rotation.y += delta * 0.12;
    if (outer.current) {
      const tx = (state.pointer.x * viewport.width) / 9;
      const ty = (state.pointer.y * viewport.height) / 9;
      outer.current.rotation.y += (tx - outer.current.rotation.y) * 0.04;
      outer.current.rotation.x += (-ty - outer.current.rotation.x) * 0.04;
    }
  });

  const wireColor = dark ? "#ffffff" : "#1e293b";
  const wireOpacity = dark ? 0.2 : 0.32;
  const outerWireColor = dark ? "#6366f1" : "#94a3b8";
  const outerWireOpacity = dark ? 0.1 : 0.18;
  const innerShellOpacity = dark ? 0.05 : 0.07;
  const lineColor = dark ? "#EE8621" : "#ED1944";
  const lineOpacity = dark ? 0.28 : 0.38;

  return (
    <group ref={outer}>
      <group ref={inner}>
        {/* en dış soluk wireframe katmanı */}
        <mesh>
          <sphereGeometry args={[1.72, 18, 18]} />
          <meshBasicMaterial
            color={outerWireColor}
            wireframe
            transparent
            opacity={outerWireOpacity}
          />
        </mesh>

        {/* ana wireframe küre */}
        <mesh>
          <icosahedronGeometry args={[radius, 5]} />
          <meshBasicMaterial
            color={wireColor}
            wireframe
            transparent
            opacity={wireOpacity}
          />
        </mesh>

        {/* içi dolduran çok soluk renk */}
        <mesh>
          <sphereGeometry args={[radius * 0.985, 32, 32]} />
          <meshBasicMaterial
            color="#ED1944"
            transparent
            opacity={innerShellOpacity}
          />
        </mesh>

        {/* düğümler (node) */}
        {nodes.map((p, i) => {
          const accent = i % 5 === 0;
          return (
            <mesh key={i} position={p}>
              <sphereGeometry args={[accent ? 0.055 : 0.038, 12, 12]} />
              <meshStandardMaterial
                color={accent ? "#EE8621" : "#ED1944"}
                emissive={accent ? "#EE8621" : "#ED1944"}
                emissiveIntensity={dark ? (accent ? 1.1 : 0.7) : accent ? 0.6 : 0.4}
                roughness={0.3}
                metalness={0.4}
              />
            </mesh>
          );
        })}

        {/* bağlantı çizgileri */}
        <lineSegments geometry={lineGeo}>
          <lineBasicMaterial color={lineColor} transparent opacity={lineOpacity} />
        </lineSegments>
      </group>
    </group>
  );
}

export default function HeroScene() {
  const theme = useTheme();
  const dark = theme === "dark";
  return (
    <Canvas
      dpr={[1, 1.8]}
      camera={{ position: [0, 0, 5.2], fov: 42 }}
      gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      style={{ pointerEvents: "none" }}
    >
      <ambientLight intensity={dark ? 0.55 : 0.75} />
      <directionalLight position={[3, 3, 3]} intensity={dark ? 0.7 : 0.9} color="#ffeeea" />
      <pointLight position={[-3, -2, 2]} intensity={dark ? 1.2 : 0.8} color="#EE8621" />
      <pointLight position={[3, 2, -2]} intensity={dark ? 0.9 : 0.6} color="#6366f1" />
      <Suspense fallback={null}>
        <Globe dark={dark} />
      </Suspense>
    </Canvas>
  );
}